import { useEffect, useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    ReactFlow, Background, Controls, Position, Handle, MarkerType,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
    X, Workflow, Compass, BookOpen, ClipboardList, PhoneForwarded, PhoneOff, Zap, Lock, MessageSquare, Wrench,
} from 'lucide-react';
import { fetchAvatarFlowConfig } from '../../../api/ivrAgents.js';
import { overlayDismiss } from '../../../lib/overlayDismiss';

// label 走 i18n（key = flow.nodeType.<type>，fallback = labelKey）
const NODE_META = {
    router:         { icon: Compass, color: '#c84a2c', accentBg: '#f0bfb8', labelKey: 'router' },
    knowledge:      { icon: BookOpen, color: '#7b9659', accentBg: '#d9e3c9', labelKey: 'knowledge' },
    apiTool:        { icon: Zap, color: '#c4892d', accentBg: '#f3e2bf', labelKey: 'apiTool' },
    formCollection: { icon: ClipboardList, color: '#7059a4', accentBg: '#d4c8e3', labelKey: 'formCollection' },
    auth:           { icon: Lock, color: '#5e7a91', accentBg: '#c8d8e3', labelKey: 'auth' },
    transfer:       { icon: PhoneForwarded, color: '#c4674a', accentBg: '#f3d7c8', labelKey: 'transfer' },
    end:            { icon: PhoneOff, color: '#6e6358', accentBg: '#efe6d4', labelKey: 'end' },
    whatsapp:       { icon: MessageSquare, color: '#5e7a4e', accentBg: '#d9e3c9', labelKey: 'whatsapp' },
};

// 解析 node-type 顯示名稱（i18n，fallback 到 type 本身）
function nodeTypeLabel(t, type) {
    const meta = NODE_META[type];
    return meta ? t(`flow.nodeType.${meta.labelKey}`, { defaultValue: type }) : type;
}

// 跟 LiveNodeConfigBuilder 同邏輯：推每個 node 給 Gemini Live 的 tool 名稱
function inferToolsForNode(node, allNodes) {
    const tools = [];
    if (node.type === 'router') {
        // router 自己不發 tool；scenarios 透過 navigate(scenarioId) 切換到對應節點
        // 不過事實上 Live mode 把所有節點的 tool 全 register 在 router session 裡
    }
    if (node.type === 'knowledge') tools.push('search_knowledge_base');
    if (node.type === 'apiTool') {
        const apis = node.apiToolConfig?.tools || node.apiToolConfig?.apis || [];
        apis.forEach(a => tools.push(a.name || a.functionName));
    }
    if (node.type === 'formCollection') tools.push(`submit_form_${node.id}`);
    if (node.type === 'transfer') tools.push('transfer_to_human');
    if (node.type === 'end') tools.push('end_call');
    return tools.filter(Boolean);
}

export function FlowViewerModal({ open, onClose, avatarId, avatarName }) {
    const { t } = useTranslation('wsModals');
    const [flow, setFlow] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedNodeId, setSelectedNodeId] = useState(null);

    useEffect(() => {
        if (!open || !avatarId) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        setSelectedNodeId(null);
        fetchAvatarFlowConfig(avatarId)
            .then((r) => {
                if (!cancelled) {
                    const f = r?.data?.ivrFlowConfig || r?.ivrFlowConfig || null;
                    setFlow(f);
                    const router = f?.nodes?.find((n) => n.type === 'router');
                    if (router) setSelectedNodeId(router.id);
                }
            })
            .catch((e) => { if (!cancelled) setError(e); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [open, avatarId]);

    const onNodeClick = useCallback((_, node) => setSelectedNodeId(node.id), []);

    const { rfNodes, rfEdges } = useMemo(
        () => buildRfGraph(flow, selectedNodeId, t),
        [flow, selectedNodeId, t]
    );

    if (!open) return null;
    const selectedNode = flow?.nodes?.find((n) => n.id === selectedNodeId) || null;
    const outEdges = (flow?.edges || []).filter((e) => e.source === selectedNodeId);
    const inEdges = (flow?.edges || []).filter((e) => e.target === selectedNodeId);

    return (
        <div className="fixed inset-0 bg-ink-900/30 backdrop-blur-sm z-50 grid place-items-center p-4" {...overlayDismiss(onClose)}>
            <div
                className="bg-cream-50 border border-cream-200 rounded-lg shadow-xl w-full max-w-6xl h-[88vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between px-6 py-3 border-b border-cream-200 shrink-0">
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-400">{t('flow.previewLabel')}</div>
                        <h2 className="heading-display text-base font-semibold text-ink-900">
                            {avatarName || avatarId}
                        </h2>
                    </div>
                    <button onClick={onClose} className="size-8 inline-flex items-center justify-center rounded-md text-ink-400 hover:text-ink-700 hover:bg-cream-100 transition">
                        <X size={16} />
                    </button>
                </div>

                <div className="flex flex-1 min-h-0">
                    <div className="flex-1 min-w-0 border-r border-cream-200 bg-cream-100/40 relative">
                        {loading && <div className="absolute inset-0 grid place-items-center text-ink-400 text-sm italic">{t('common:state.loading')}</div>}
                        {error && <div className="absolute inset-0 grid place-items-center text-sm text-danger-500">{error.message}</div>}
                        {!loading && !error && (
                            <ReactFlow
                                nodes={rfNodes}
                                edges={rfEdges}
                                nodeTypes={NODE_TYPES}
                                onNodeClick={onNodeClick}
                                fitView
                                fitViewOptions={{ padding: 0.2 }}
                                nodesDraggable={false}
                                nodesConnectable={false}
                                elementsSelectable
                                proOptions={{ hideAttribution: true }}
                            >
                                <Background color="#c8b794" gap={20} size={1.2} />
                                <Controls showInteractive={false} className="!shadow-none !border !border-cream-200 !rounded-md !bg-white" />
                            </ReactFlow>
                        )}
                    </div>

                    <div className="w-[440px] shrink-0 overflow-y-auto p-5">
                        {selectedNode ? (
                            <NodeDetail
                                node={selectedNode}
                                outEdges={outEdges}
                                inEdges={inEdges}
                                routerNode={flow?.nodes?.find(n => n.type === 'router')}
                                allNodes={flow?.nodes || []}
                            />
                        ) : (
                            <div className="text-sm text-ink-400 italic text-center py-12">{t('flow.selectHint')}</div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

// ─── React Flow custom node ────────────────────────────────────────

function FlowNode({ data }) {
    const meta = NODE_META[data.type] || { icon: Workflow, color: '#6e6358', accentBg: '#efe6d4' };
    const Icon = meta.icon;
    return (
        <div
            className="px-3 py-2.5 rounded-md border-2 bg-white shadow-sm min-w-[180px] cursor-pointer transition"
            style={{
                borderColor: data.selected ? meta.color : '#efe6d4',
                boxShadow: data.selected ? `0 0 0 3px ${meta.accentBg}` : undefined,
            }}
        >
            <Handle type="target" position={Position.Left} className="!size-2 !bg-cream-300 !border-cream-400" />
            <div className="flex items-center gap-2">
                <div className="size-7 rounded-md flex items-center justify-center shrink-0" style={{ background: meta.accentBg }}>
                    <Icon size={14} strokeWidth={1.8} style={{ color: meta.color }} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-semibold text-ink-900 leading-tight truncate">{data.label}</div>
                    <div className="font-mono text-[9px] uppercase tracking-wider text-ink-400 mt-0.5">{data.type}</div>
                </div>
            </div>
            <Handle type="source" position={Position.Right} className="!size-2 !bg-cream-300 !border-cream-400" />
        </div>
    );
}

const NODE_TYPES = { flowNode: FlowNode };

function buildRfGraph(flow, selectedNodeId, t) {
    if (!flow) return { rfNodes: [], rfEdges: [] };
    const nodes = flow.nodes || [];
    const edges = flow.edges || [];

    const incoming = new Map();
    nodes.forEach((n) => incoming.set(n.id, 0));
    edges.forEach((e) => incoming.set(e.target, (incoming.get(e.target) || 0) + 1));
    const roots = nodes.filter((n) => (incoming.get(n.id) || 0) === 0).map((n) => n.id);
    const level = new Map();
    const queue = roots.map((id) => ({ id, lv: 0 }));
    while (queue.length) {
        const { id, lv } = queue.shift();
        if (level.has(id) && level.get(id) >= lv) continue;
        level.set(id, lv);
        edges.filter((e) => e.source === id).forEach((e) => queue.push({ id: e.target, lv: lv + 1 }));
    }
    nodes.forEach((n) => { if (!level.has(n.id)) level.set(n.id, 0); });

    const byLevel = new Map();
    nodes.forEach((n) => {
        const lv = level.get(n.id);
        if (!byLevel.has(lv)) byLevel.set(lv, []);
        byLevel.get(lv).push(n);
    });

    const X_GAP = 260, Y_GAP = 120;
    const rfNodes = nodes.map((n) => {
        const lv = level.get(n.id);
        const siblings = byLevel.get(lv);
        const idx = siblings.indexOf(n);
        const total = siblings.length;
        const yOffset = (idx - (total - 1) / 2) * Y_GAP;
        return {
            id: n.id,
            type: 'flowNode',
            position: { x: lv * X_GAP, y: yOffset },
            data: { label: n.displayLabel || nodeTypeLabel(t, n.type) || n.id, type: n.type, selected: n.id === selectedNodeId },
            selected: n.id === selectedNodeId,
        };
    });

    // 邊不放 label（scenarioId 在右欄 NodeDetail 看；canvas 純粹顯示拓樸）
    const rfEdges = edges.map((e, i) => ({
        id: `${e.source}-${e.target}-${i}`,
        source: e.source,
        target: e.target,
        type: 'smoothstep',
        style: { stroke: '#c8b794', strokeWidth: 1.5 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#c8b794', width: 14, height: 14 },
    }));

    return { rfNodes, rfEdges };
}

// ─── 節點屬性面板 ────────────────────────────────────────────────────

function NodeDetail({ node, outEdges, inEdges, routerNode, allNodes }) {
    const { t } = useTranslation('wsModals');
    const meta = NODE_META[node.type] || { icon: Workflow, color: '#6e6358' };
    const Icon = meta.icon;
    const ai = node.agentInstruction;
    const tools = inferToolsForNode(node, allNodes);

    // 找從哪個 scenarioId 進來（in-edge condition）→ 對應 router scenario 詳情
    const triggeringScenarios = inEdges
        .map(e => e.condition?.scenarioId)
        .filter(Boolean)
        .map(sid => (routerNode?.routerConfig?.scenarios || []).find(s => s.id === sid))
        .filter(Boolean);

    return (
        <div className="space-y-4">
            <div className="flex items-center gap-3 pb-3 border-b border-cream-200">
                <div className="size-10 rounded-md flex items-center justify-center" style={{ background: meta.accentBg }}>
                    <Icon size={20} strokeWidth={1.6} style={{ color: meta.color }} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="heading-display text-base font-semibold text-ink-900 truncate">
                        {node.displayLabel || nodeTypeLabel(t, node.type)}
                    </div>
                    <code className="font-mono text-[10px] text-ink-400">{node.id} · {node.type}</code>
                </div>
            </div>

            {/* 觸發條件（從哪個 scenario 進來） */}
            {triggeringScenarios.length > 0 && (
                <Section title={t('flow.section.trigger')}>
                    {triggeringScenarios.map((s) => (
                        <div key={s.id} className="rounded-md bg-cream-100/60 border border-cream-200 p-2.5 mb-1.5">
                            <div className="flex items-center gap-1.5 mb-1">
                                <code className="font-mono text-[10px] text-accent-700">{s.id}</code>
                                <span className="text-sm font-medium text-ink-900">{s.name}</span>
                            </div>
                            {s.description && <div className="text-xs text-ink-500 mb-1.5">{s.description}</div>}
                            {s.keywords?.length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                    {s.keywords.map((k) => (
                                        <span key={k} className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-white border border-cream-300 text-ink-500">
                                            {k}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </Section>
            )}

            {/* agentInstruction */}
            {ai && (
                <div className="space-y-3">
                    {ai.role && <Field label={t('flow.field.role')} value={ai.role} />}
                    {ai.goal && <Field label={t('flow.field.goal')} value={ai.goal} />}
                    {ai.instructions && <Field label={t('flow.field.instructions')} value={ai.instructions} multiline />}
                    {ai.constraints && <Field label={t('flow.field.constraints')} value={ai.constraints} multiline />}
                </div>
            )}

            {/* Router 本身：列出所有情境分支詳情 */}
            {node.type === 'router' && node.routerConfig?.scenarios?.length > 0 && (
                <Section title={t('flow.section.scenarios', { n: node.routerConfig.scenarios.length })}>
                    {node.routerConfig.scenarios.map((s) => (
                        <div key={s.id} className="rounded-md bg-cream-100/60 border border-cream-200 p-2.5 mb-1.5">
                            <div className="flex items-center gap-1.5 mb-1">
                                <code className="font-mono text-[10px] text-accent-700">{s.id}</code>
                                <span className="text-sm font-medium text-ink-900">{s.name}</span>
                            </div>
                            {s.description && <div className="text-xs text-ink-500 mb-1.5">{s.description}</div>}
                            {s.keywords?.length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                    {s.keywords.map((k) => (
                                        <span key={k} className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-white border border-cream-300 text-ink-500">
                                            {k}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </Section>
            )}

            {/* Knowledge */}
            {node.type === 'knowledge' && node.knowledgeConfig && (
                <Section title={t('flow.section.kbBinding')}>
                    {(node.knowledgeConfig.knowledgeBases || node.knowledgeConfig.knowledgeBaseIds?.map(id => ({ id, name: id })) || []).map((kb) => (
                        <div key={kb.id} className="rounded-md bg-cream-100/60 border border-cream-200 p-2 mb-1">
                            <div className="text-sm text-ink-900">{kb.name || t('flow.unnamed')}</div>
                            <code className="font-mono text-[10px] text-ink-400">{kb.id}</code>
                        </div>
                    ))}
                    {node.knowledgeConfig.vectorEnabled && (
                        <div className="font-mono text-[10px] uppercase tracking-wider text-success-500 mt-2">
                            ✓ vector search · topK={node.knowledgeConfig.vectorTopK || 6}
                        </div>
                    )}
                    {node.knowledgeConfig.notFoundMessage && (
                        <div className="mt-2">
                            <div className="font-mono text-[10px] uppercase tracking-wider text-ink-400 mb-0.5">{t('flow.notFoundFallback')}</div>
                            <div className="text-xs text-ink-700 italic">「{node.knowledgeConfig.notFoundMessage}」</div>
                        </div>
                    )}
                </Section>
            )}

            {/* FormCollection */}
            {node.type === 'formCollection' && node.formCollectionConfig && (
                <Section title={node.formCollectionConfig.formName || t('flow.section.form')}>
                    {node.formCollectionConfig.toolDescription && (
                        <div className="text-xs text-ink-500 italic mb-2 leading-relaxed">
                            {node.formCollectionConfig.toolDescription}
                        </div>
                    )}
                    <div className="space-y-1.5">
                        {(node.formCollectionConfig.fields || []).map((f) => (
                            <div key={f.name} className="rounded-md bg-cream-100/60 border border-cream-200 px-2.5 py-1.5">
                                <div className="flex items-center gap-2">
                                    <code className="font-mono text-[10px] text-ink-500 shrink-0">{f.name}</code>
                                    <span className="text-sm text-ink-900 flex-1">{f.label}</span>
                                    <span className="font-mono text-[10px] uppercase tracking-wider text-accent-700 shrink-0">{f.type}</span>
                                    {f.required && <span className="font-mono text-[9px] uppercase text-danger-500 shrink-0">required</span>}
                                </div>
                                {f.promptText && (
                                    <div className="text-[11px] text-ink-500 mt-1 italic">{t('flow.fieldPromptPrefix')}{f.promptText}</div>
                                )}
                                {f.options?.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mt-1">
                                        {f.options.map((o, i) => (
                                            <span key={i} className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-white border border-cream-300 text-ink-500">
                                                {typeof o === 'string' ? o : o.label || o.value}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </Section>
            )}

            {/* ApiTool */}
            {node.type === 'apiTool' && node.apiToolConfig && (
                <Section title={t('flow.section.apiTool')}>
                    {(node.apiToolConfig.tools || node.apiToolConfig.apis || []).map((api, i) => (
                        <div key={i} className="rounded-md bg-cream-100/60 border border-cream-200 px-2.5 py-1.5 mb-1.5">
                            <div className="flex items-center gap-1.5">
                                <span className={[
                                    'font-mono text-[10px] uppercase px-1.5 py-0.5 rounded shrink-0',
                                    (api.method || 'POST') === 'GET'
                                        ? 'bg-success-100 text-success-500'
                                        : 'bg-accent-50 text-accent-700',
                                ].join(' ')}>{api.method || 'POST'}</span>
                                <code className="font-mono text-xs text-ink-900 truncate">{api.name || api.functionName}</code>
                                {api.isTerminal && <span className="font-mono text-[9px] uppercase text-warn-500">terminal</span>}
                                {api.saveAsLead && <span className="font-mono text-[9px] uppercase text-success-500">lead</span>}
                            </div>
                            {api.description && <div className="text-xs text-ink-500 mt-1">{api.description}</div>}
                            {api.url && <code className="font-mono text-[10px] text-ink-400 block mt-0.5 truncate">{api.url}</code>}
                        </div>
                    ))}
                </Section>
            )}

            {/* Transfer */}
            {node.type === 'transfer' && node.transferConfig && (
                <Section title={t('flow.section.transfer')}>
                    {(node.transferConfig.scenarios || []).map((s) => (
                        <div key={s.id} className="rounded-md bg-cream-100/60 border border-cream-200 p-2.5 mb-1.5">
                            <div className="flex items-center gap-1.5 mb-0.5">
                                <code className="font-mono text-[10px] text-accent-700">{s.id}</code>
                                <span className="text-sm font-medium text-ink-900">{s.name}</span>
                            </div>
                            {s.description && <div className="text-xs text-ink-500 mb-1">{s.description}</div>}
                            <div className="text-xs text-ink-700">
                                {s.channel === 'extension' && (
                                    <>{t('flow.extension')} <code className="font-mono text-ink-900">{s.extension}</code></>
                                )}
                                {s.channel === 'sip' && (
                                    <>SIP <code className="font-mono text-ink-900">{s.sipUri}</code></>
                                )}
                            </div>
                        </div>
                    ))}
                    {node.transferConfig.defaultExt && (
                        <div className="text-xs text-ink-700">
                            {t('flow.defaultExt')} <code className="font-mono text-ink-900">{node.transferConfig.defaultExt}</code>
                        </div>
                    )}
                </Section>
            )}

            {/* 工具列表 — 該 node AI 可呼叫的工具 */}
            {tools.length > 0 && (
                <Section title={t('flow.section.tools')}>
                    <div className="flex flex-wrap gap-1.5">
                        {tools.map((tool) => (
                            <span key={tool} className="inline-flex items-center gap-1 font-mono text-[10px] px-2 py-1 rounded bg-white border border-cream-300 text-ink-700">
                                <Wrench size={10} strokeWidth={2} className="text-accent-700" />
                                {tool}
                            </span>
                        ))}
                    </div>
                </Section>
            )}

            {/* 連到 */}
            {outEdges.length > 0 && (
                <Section title={t('flow.section.next')}>
                    <div className="space-y-1">
                        {outEdges.map((e, i) => (
                            <div key={i} className="text-xs text-ink-500 inline-flex items-center gap-1.5">
                                {e.condition?.scenarioId && (
                                    <code className="font-mono text-[10px] text-accent-700">{e.condition.scenarioId}</code>
                                )}
                                <span className="text-ink-400">→</span>
                                <code className="font-mono text-[10px] text-ink-900">{e.target}</code>
                            </div>
                        ))}
                    </div>
                </Section>
            )}
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-ink-400 mb-1.5">{title}</div>
            {children}
        </div>
    );
}

function Field({ label, value, multiline }) {
    return (
        <div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-ink-400 mb-0.5">{label}</div>
            {multiline ? (
                <pre className="text-xs text-ink-700 whitespace-pre-wrap font-sans leading-relaxed">{value}</pre>
            ) : (
                <div className="text-sm text-ink-900">{value}</div>
            )}
        </div>
    );
}
